import { FC, useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { CallIntelTooltip, generateTrackingId, useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { DataAnalystRolePromo } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, TextLink, useAlert } from '@frontend/design-system';
import { URLs } from '../../../constants';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { useFollowUpTabCount } from '../hooks/use-follow-up-count';
import { TaskReasonCard } from './task-reason-card';

const TASK_TYPE_ORDER: Exclude<CallIntelTypes.TaskType, CallIntelTypes.TaskType.TYPE_UNKNOWN>[] = [
  CallIntelTypes.TaskType.TYPE_SCHEDULING,
  CallIntelTypes.TaskType.TYPE_WAITLIST,
  CallIntelTypes.TaskType.TYPE_PATIENT_CARE,
  CallIntelTypes.TaskType.TYPE_INSURANCE,
  CallIntelTypes.TaskType.TYPE_BILLING,
  CallIntelTypes.TaskType.TYPE_OTHER,
];

const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const FollowUpTasksByReasonList: FC = () => {
  const { setIsLoadingAggregatedData, setSubView } = useCallIntelShallowStore(
    'setIsLoadingAggregatedData',
    'setSubView'
  );
  const { filters } = useActiveFilters();
  const { t } = useTranslation('call-intelligence');
  const alert = useAlert();
  const navigate = useNavigate();

  const { isDemoAccount, showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { taskCategoryEnabledMap } = CallIntelligenceV2.Hooks.useAggregateTaskSettings({
    locationIds: filters.locations,
    checkTaskCategoriesAggregate: true,
    isDemo: isDemoAccount,
  });

  const { data: overviewData, isLoading: isOverviewLoading } = CallIntelligenceV2.Queries.useGetCallsOverviewQuery({
    request: {
      ...CallIntelUtils.generateChartsPayload({ filters }),
      includesAnalysis: [
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
        CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
        CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
        CallIntelTypes.IncludesAnalysis.FOLLOWUP_TASKS,
      ],
      includesHydration: [],
    },
    options: {
      enabled: !!filters.locations?.length,
      retry: false,
      cacheTime: DEFAULT_CACHE_TIME,
      staleTime: DEFAULT_CACHE_TIME,
      onError: (error) => {
        alert.error(t("Couldn't fetch calls overview data. Please try again."));
        console.error('Error fetching calls overview data:', error);
      },
    },
  });

  useEffect(() => {
    setIsLoadingAggregatedData?.(isOverviewLoading);
  }, [isOverviewLoading, setIsLoadingAggregatedData]);

  const { pendingFollowUpCount } = useFollowUpTabCount();

  const filledTaskTypes = useMemo(() => {
    const enabledTaskTypes = TASK_TYPE_ORDER.filter((type) => taskCategoryEnabledMap?.[type] !== false);
    return CallIntelUtils.fillTaskTypes(overviewData?.taskTypes ?? {}, enabledTaskTypes);
  }, [overviewData?.taskTypes, taskCategoryEnabledMap]);

  const handleViewAllClick = useCallback(() => {
    setSubView({ id: 'all', type: 'follow-up' });
    navigate({
      to: `${URLs.CALL_INTEL_BASE}/follow-ups/all`,
    });
  }, [navigate, setSubView]);

  const isLoading = isOverviewLoading;

  return (
    <div css={defaultStyles.container}>
      <div css={defaultStyles.header}>
        <div css={defaultStyles.headerTitle}>
          {showDemoChipAndBanner && <DemoChip />}
          <Heading level={3}>{t('Follow-up Tasks by Reason')}</Heading>
          <CallIntelTooltip tip='followUpSummary' />
        </div>
        <TextLink
          weight='bold'
          onClick={handleViewAllClick}
          trackingId={generateTrackingId({
            component: 'follow-up-tasks-by-reason-list',
            context: 'view-all-button',
            isDemoAccount,
          })}
        >
          {t('View All Follow-ups ({{pendingFollowUpCount}})', { pendingFollowUpCount })}
        </TextLink>
      </div>

      <DataAnalystRolePromo locationIds={filters.locations ?? []} source='follow-ups' />

      <div css={defaultStyles.cardContainer}>
        {Object.entries(filledTaskTypes).map(([key, value]) => (
          <TaskReasonCard
            key={key}
            count={value}
            isLoading={isLoading}
            label={dataLabels?.taskTypes?.[key] ?? key}
            type={key as CallIntelTypes.TaskType}
          />
        ))}
      </div>
    </div>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(1)};
    }
  `,
  header: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    align-items: center;
    justify-content: space-between;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  headerTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  cardContainer: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(332px, 1fr));
    gap: ${theme.spacing(2)};

    @container (max-width: ${breakpoints.small.max}px) {
      gap: ${theme.spacing(1)};
    }
  `,
};
